import {ClientData} from "./types";

export const profileSettingsItems: (keyof ClientData)[] = ['companyType', 'jurisdictions', 'topics', 'documentTypes', 'notificationFormat']

// COM-638/COM-637: the fields shown in the onboarding UI (wizard steps and
// settings-page blocks). `notificationFormat` stays out of this list on
// purpose — per Confluence 700186650 the "Notification Format" step must be
// hidden in the first implementation, but the field itself must stay in
// ClientData/profileSettingsItems/the payload/the DB. Both ClientDataDialog
// and SettingsPage derive their rendered blocks from this constant so they
// cannot drift apart on which steps are visible.
export const visibleProfileSettingsItems: (keyof ClientData)[] = ['companyType', 'jurisdictions', 'topics', 'documentTypes']

export const getPrefilledClientData = <T extends keyof ClientData, V>(
  value: V
): Record<keyof ClientData, V> => {
  return profileSettingsItems.reduce((res, key) => {
    // @ts-ignore
    res[key] = Array.isArray(value) ? [...value] : value;
    return res;
  }, {} as Record<keyof ClientData, V>);
};

// COM-638/COM-637 (2026-08-14): these three lists were replaced verbatim
// with the Jira COM-637 / Confluence 700186650 spec lists. This is a
// deliberate, non-migrating replacement: values a project saved under the
// OLD lists (e.g. "Bank", "Fintech", "KYC / AML") are not present in the
// new lists below, so `filterProfileSettings` classifies them as custom
// options and they keep rendering as chips instead of checkboxes. That is
// the intended outcome — no data loss, but also no mapping table from old
// values to new ones. Do not add one without re-checking with the team.
const COMPANY_TYPES = [
  'Banks',
  'FinTechs',
  'Payment Service Providers (PSPs)',
  'Investment Firms',
  'Asset Managers / Funds',
  'Crypto-asset Service Providers (CASPs)',
  'Advisory, Legal & RegTech Providers',
];

const JURISDICTIONS = [
  'United Kingdom',
  'European Union',
  'United States',
  'Switzerland',
];

const TOPICS = [
  'AML / KYC (Anti-Money Laundering / Know Your Customer)',
  'Payments & PSD2 / PSD3',
  'Open Banking / Open Finance',
  'Consumer Duty & Consumer Protection',
  'Capital Requirements / Prudential Regulation (CRD/CRR, Basel III/IV)',
  'Securities & Market Abuse (MiFID II / MiFIR, MAR, EMIR)',
  'Data Privacy & GDPR',
  'AI & Model Risk (AI Act, model governance)',
  'Crypto & MiCA (Markets in Crypto-Assets Regulation)',
  'Sustainability & ESG (SFDR, CSRD, EU Taxonomy)',
  'Outsourcing & Third-Party Risk Management',
  'Operational Resilience & ICT Risk (DORA)',
  'Financial Crime / Sanctions Compliance',
  'Risk Management & Stress Testing',
];

const DOCUMENT_TYPES = [
  'Regulation / Rule / Standard',
  'Directive / Act / Decision',
  'Technical Standard (RTS/ITS, Basel Standards)',
  'Guideline / Guidance / Principle',
  'Recommendation / Best Practice',
  'Consultation Paper / Draft',
  'Opinion',
  'Q&A / Interpretative Note',
  'Report / Risk Report / Assessment / Stress Test Report',
  'Statement / Public Statement / Speech/News',
  'Policy / Framework',
];

const NOTIFICATION_FORMATS = [
  'Weekly email summary',
  'Real-time in system',
  'Slack / Jira integration',
  'CSV / API format',
];

export const profileSettingsOptions: Record<keyof ClientData, string[]> = {
  'companyType': COMPANY_TYPES,
  'jurisdictions': JURISDICTIONS,
  'topics': TOPICS,
  'documentTypes': DOCUMENT_TYPES,
  'notificationFormat': NOTIFICATION_FORMATS,
}

// COM-636: the single source of truth for "does this project need the
// onboarding prompt?". Extracted so the two previously-separate auto-open
// effects (Header.tsx's dead `shouldAskClientData` check and
// UserProfile.tsx's `companyType` check) collapse into one call site
// (OnboardingGate.tsx). `companyType` is the field the backend sets once a
// project completes onboarding; an empty array means it never has. `[]` is
// truthy in JS, so this only matches "present but empty", not "absent" —
// that mirrors the pre-existing UserProfile.tsx condition, which is the one
// that actually fired in production (see AGENTS.md-linked investigation
// notes in the COM-636 PR description).
export const shouldPromptForClientData = (companyType?: string[] | null): boolean =>
  Array.isArray(companyType) && companyType.length === 0;

export const filterProfileSettings = (clientData: ClientData) => {
  return Object.keys(clientData).reduce((res,key) => {
    const items = clientData[key as keyof ClientData];
    return {
      ...res,
      [key]: items.filter((item) => profileSettingsOptions[key as keyof ClientData].includes(item))
    }
  }, {} as ClientData)
}

